/**
 * CalendarBarWidget - Compact calendar strip showing current week
 */

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { WidgetWrapper } from './WidgetWrapper';
import { AdaptiveModal } from '../AdaptiveModal';
import { useWindows } from '../../contexts/WindowsContext';
import type { CalendarBarWidgetConfig } from '../../types/widgets';

interface CalendarBarWidgetProps {
  config: CalendarBarWidgetConfig;
  widgetId: string;
  windowId: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function CalendarBarWidget({ config, widgetId, windowId }: CalendarBarWidgetProps) {
  const [expanded, setExpanded] = useState(false);
  const { isMobile } = useWindows();
  const today = new Date();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());

  const days = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + i);
    return date;
  });

  const dayNames = isMobile
    ? ['S', 'M', 'T', 'W', 'T', 'F', 'S']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const weekContent = (
      <div className="flex items-center justify-between px-2 py-3">
        <button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
          <ChevronLeft className="w-4 h-4 text-gray-500" />
        </button>

        <div className="flex gap-1 flex-1 justify-center">
          {days.map((date, i) => {
            const isToday = date.toDateString() === today.toDateString();
            return (
              <div
                key={i}
                className={`flex flex-col items-center px-2 py-1 rounded cursor-pointer transition-colors ${
                  isToday
                    ? 'bg-blue-500 text-white'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <span className={`text-xs ${isToday ? 'text-blue-100' : 'text-gray-500'}`}>
                  {dayNames[i]}
                </span>
                <span className={`text-sm font-medium ${isToday ? 'text-white' : 'text-gray-800 dark:text-gray-200'}`}>
                  {date.getDate()}
                </span>
              </div>
            );
          })}
        </div>

        <button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
          <ChevronRight className="w-4 h-4 text-gray-500" />
        </button>
      </div>
  );

  return (
    <>
      <WidgetWrapper title="This Week" widgetId={widgetId} windowId={windowId} onHeaderClick={() => setExpanded(true)}>
        {weekContent}
      </WidgetWrapper>
      {expanded && (
        <AdaptiveModal isOpen onClose={() => setExpanded(false)} title="This Week" size="lg">
          <div className="max-h-[80vh] overflow-auto">{weekContent}</div>
        </AdaptiveModal>
      )}
    </>
  );
}
